class AccordionGroup extends HTMLElement {
  constructor() {
    super();

    this.items = [];
    this.popupMediaQuery = null;
    this.popupEl = null;
    this.popupPanel = null;
    this.activePopupItem = null;
    this.closeTimer = null;

    this.handleEscape = this.handleEscape.bind(this);
    this.handleMediaChange = this.handleMediaChange.bind(this);
  }

  connectedCallback() {
    this.items = Array.from(this.querySelectorAll(".accordion__item"));

    this.setupPopupMediaQuery();
    this.bindAccordionItems();
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this.handleEscape);

    if (this.popupMediaQuery) {
      this.popupMediaQuery.removeEventListener(
        "change",
        this.handleMediaChange,
      );
    }

    if (this.popupEl) {
      this.popupEl.remove();
    }
  }

  setupPopupMediaQuery() {
    const breakpoint = this.getAttribute("data-popup-mobile");

    if (!breakpoint) return;

    const normalizedBreakpoint = /^\d+$/.test(breakpoint)
      ? `${breakpoint}px`
      : breakpoint;

    this.popupMediaQuery = window.matchMedia(
      `(max-width: ${normalizedBreakpoint})`,
    );

    this.popupMediaQuery.addEventListener("change", this.handleMediaChange);
  }

  bindAccordionItems() {
    this.items.forEach((item) => {
      const header = item.querySelector(".accordion__header");
      const content = item.querySelector(".accordion__content");

      if (!header || !content) return;

      header.setAttribute("aria-expanded", "false");

      header.addEventListener("click", () => {
        if (this.isPopupMode()) {
          this.openPopup(item);
          return;
        }

        this.closePopup(true);
        this.closeOthers(item);
        this.toggleItem(item, content);
      });
    });
  }

  isPopupMode() {
    return Boolean(this.popupMediaQuery && this.popupMediaQuery.matches);
  }

  handleMediaChange() {
    this.closePopup(true);

    if (this.isPopupMode()) {
      this.closeAllItems();
    }
  }

  closeOthers(currentItem) {
    this.items.forEach((item) => {
      if (item !== currentItem) {
        this.closeItem(item);
      }
    });
  }

  closeAllItems() {
    this.items.forEach((item) => {
      this.closeItem(item);
    });
  }

  closeItem(item) {
    const header = item.querySelector(".accordion__header");
    const content = item.querySelector(".accordion__content");

    item.classList.remove("active");

    if (header) {
      header.setAttribute("aria-expanded", "false");
    }

    if (content) {
      content.style.maxHeight = null;
    }
  }

  toggleItem(item, content) {
    const isActive = item.classList.toggle("active");
    const header = item.querySelector(".accordion__header");

    if (header) {
      header.setAttribute("aria-expanded", isActive ? "true" : "false");
    }

    if (isActive) {
      content.style.maxHeight = `${content.scrollHeight}px`;
    } else {
      content.style.maxHeight = null;
    }
  }

  createPopupShell() {
    this.popupEl = document.createElement("div");
    this.popupEl.className = "accordion-popup";
    this.popupEl.hidden = true;

    this.popupEl.innerHTML = `
      <div class="accordion-popup__overlay" data-accordion-popup-close></div>
      <div class="accordion-popup__panel" role="dialog" aria-modal="true"></div>
    `;

    this.popupPanel = this.popupEl.querySelector(".accordion-popup__panel");

    this.popupEl
      .querySelector("[data-accordion-popup-close]")
      .addEventListener("click", () => this.closePopup());

    document.body.appendChild(this.popupEl);
  }

  openPopup(item) {
    if (!this.popupEl) {
      this.createPopupShell();
    }

    this.closeAllItems();

    const originalHeader = item.querySelector(".accordion__header");
    const originalContent = item.querySelector(".accordion__content");

    if (!originalHeader || !originalContent) return;

    const popupItem = document.createElement("div");
    popupItem.className = `${item.className} active accordion-popup__item`;

    const popupHeader = originalHeader.cloneNode(true);
    const popupContent = originalContent.cloneNode(true);

    this.removeDuplicateIds(popupHeader);
    this.removeDuplicateIds(popupContent);

    popupHeader.setAttribute("aria-expanded", "true");

    popupContent.style.maxHeight = "none";
    popupContent.style.overflow = "visible";

    popupHeader.addEventListener("click", () => {
      this.closePopup();
    });

    popupItem.appendChild(popupHeader);
    popupItem.appendChild(popupContent);

    this.popupPanel.replaceChildren(popupItem);

    const originalHeaderButton = item.querySelector(".accordion__header");
    if (originalHeaderButton) {
      originalHeaderButton.setAttribute("aria-expanded", "true");
    }

    this.activePopupItem = item;

    clearTimeout(this.closeTimer);

    this.popupEl.hidden = false;
    this.popupEl.classList.remove("is-closing");

    document.body.classList.add("accordion-popup-is-open");
    document.addEventListener("keydown", this.handleEscape);

    requestAnimationFrame(() => {
      this.popupEl.classList.add("is-open");
    });
  }

  closePopup(immediate = false) {
    if (!this.popupEl || this.popupEl.hidden) return;

    const finishClose = () => {
      this.popupEl.hidden = true;
      this.popupEl.classList.remove("is-open", "is-closing");

      if (this.popupPanel) {
        this.popupPanel.replaceChildren();
      }

      if (this.activePopupItem) {
        const header = this.activePopupItem.querySelector(".accordion__header");

        if (header) {
          header.setAttribute("aria-expanded", "false");
        }
      }

      this.activePopupItem = null;

      document.body.classList.remove("accordion-popup-is-open");
      document.removeEventListener("keydown", this.handleEscape);
    };

    clearTimeout(this.closeTimer);

    if (immediate) {
      finishClose();
      return;
    }

    this.popupEl.classList.remove("is-open");
    this.popupEl.classList.add("is-closing");

    this.closeTimer = setTimeout(finishClose, 350);
  }

  handleEscape(event) {
    if (event.key === "Escape") {
      this.closePopup();
    }
  }

  removeDuplicateIds(element) {
    element.querySelectorAll("[id]").forEach((child) => {
      child.removeAttribute("id");
    });

    if (element.hasAttribute("id")) {
      element.removeAttribute("id");
    }
  }
}

customElements.define("accordion-group", AccordionGroup);
